import React, { useState, useEffect, useCallback } from 'react';
import { getManagerSummaries, getCEsForManager } from '../supportConfig';
import { getStatusConfig, normaliseStatus, isTicketDone, isInValidation, STATUS_FLOW } from '../ticketStatus';

// ─────────────────────────────────────────────────────────────────────────────
const STYLES = `
  @keyframes supFadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin       { to{transform:rotate(360deg)} }
  @keyframes pulse      { 0%,100%{opacity:1}50%{opacity:.4} }
  @keyframes slideIn    { from{opacity:0;transform:translateX(8px)} to{opacity:1;transform:translateX(0)} }
  .s-card  { transition:box-shadow .2s,transform .18s; }
  .s-card:hover  { box-shadow:0 10px 36px rgba(0,0,0,.11)!important; transform:translateY(-2px); }
  .s-row   { transition:background .14s,box-shadow .14s; cursor:pointer; }
  .s-row:hover   { background:#f8f9fc!important; box-shadow:0 4px 16px rgba(0,0,0,.07)!important; }
  .s-mgr   { transition:box-shadow .18s,transform .18s; cursor:pointer; }
  .s-mgr:hover   { box-shadow:0 8px 28px rgba(0,0,0,.12)!important; transform:translateY(-2px); }
  .s-flt   { transition:all .16s; }
  .s-flt:hover   { opacity:.85; }
`;

const API_BASE = '';

const PRIORITY = {
  HIGH:   { bg:'rgba(255,59,48,.1)',   color:'#FF3B30', dot:'#FF3B30', label:'High'   },
  MEDIUM: { bg:'rgba(255,159,10,.1)',  color:'#E6870A', dot:'#FF9F0A', label:'Medium' },
  LOW:    { bg:'rgba(52,199,89,.1)',   color:'#248A3D', dot:'#34C759', label:'Low'    },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})
      + ' · ' + d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
  } catch { return iso; }
};
const fmtHrs = h => h < 1 ? `${Math.round(h*60)}m` : h < 24 ? `${h}h` : `${Math.round(h/24)}d`;

function ageHours(t) {
  const s = t.created_at ? new Date(t.created_at).getTime() : null;
  if (!s) return null;
  const e = (isTicketDone(t.status) && t.updated_at) ? new Date(t.updated_at).getTime() : Date.now();
  return Math.round((e - s) / 36e5);
}

// ── Micro SVG icons ──────────────────────────────────────────────────────────
const Ico = {
  Refresh: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>,
  Device:  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/></svg>,
  Person:  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  Clock:   <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Alert:   <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 19h20L12 2Z"/><path d="M12 9v5"/><circle cx="12" cy="17" r="1"/></svg>,
  Check:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Chevron: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>,
  Close:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#636366" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  Team:    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
};

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, small }) {
  const s = getStatusConfig(status);
  return (
    <span style={{ fontSize: small?10:11, fontWeight:700, padding: small?'2px 8px':'3px 11px',
      borderRadius:20, background:s.bg, color:s.color,
      display:'inline-flex', alignItems:'center', gap:4, whiteSpace:'nowrap' }}>
      <span style={{ width: small?4:5, height: small?4:5, borderRadius:'50%', background:s.dot, flexShrink:0 }}/>
      {s.label}
    </span>
  );
}

function PriBadge({ priority }) {
  const p = PRIORITY[(priority||'').toUpperCase()] || PRIORITY.MEDIUM;
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
      background:p.bg, color:p.color }}>{p.label}</span>
  );
}

// ── SLA bar ──────────────────────────────────────────────────────────────────
function SLABar({ hours, done }) {
  const SLA = 48;
  const pct = Math.min(100, (hours / SLA) * 100);
  const color = done ? '#34C759' : hours > SLA ? '#FF3B30' : hours > SLA * 0.7 ? '#FF9F0A' : '#34C759';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
      <div style={{ flex:1, height:5, borderRadius:999, background:'rgba(0,0,0,.07)', overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:999, background:color,
          width:`${pct}%`, transition:'width .5s ease' }}/>
      </div>
      <span style={{ fontSize:9, fontWeight:700, color, minWidth:26, textAlign:'right' }}>{fmtHrs(hours)}</span>
    </div>
  );
}

// ── Lifecycle strip ──────────────────────────────────────────────────────────
function LifecycleStrip({ status }) {
  const s = normaliseStatus(status);
  const idx = STATUS_FLOW.indexOf(s);
  if (idx < 0) return null;
  return (
    <div style={{ display:'flex', gap:2, marginTop:8 }}>
      {STATUS_FLOW.map((stage, i) => (
        <div key={stage} title={getStatusConfig(stage).label} style={{
          flex:1, height:3, borderRadius:2,
          background: i <= idx ? getStatusConfig(s).dot : 'rgba(0,0,0,.08)',
        }}/>
      ))}
    </div>
  );
}

// ── Ticket Timeline ───────────────────────────────────────────────────────────
function Timeline({ ticket }) {
  const events = [];
  if (ticket.created_at) events.push({ label:'Created', time:ticket.created_at, color:'#007AFF' });
  if (ticket.start_time)  events.push({ label:'Work Started', time:ticket.start_time, color:'#FF9F0A' });
  if (ticket.resolved_at) events.push({ label:'Resolved', time:ticket.resolved_at, color:'#34C759' });
  if (ticket.closed_at)   events.push({ label:'Closed', time:ticket.closed_at, color:'#34C759' });
  if (events.length < 2) return null;
  return (
    <div style={{ marginTop:12, position:'relative', paddingLeft:16 }}>
      <div style={{ position:'absolute', left:6, top:4, bottom:4, width:1, background:'rgba(0,0,0,.08)' }}/>
      {events.map((e, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, position:'relative' }}>
          <div style={{ width:10, height:10, borderRadius:'50%', background:e.color,
            flexShrink:0, marginLeft:-4, zIndex:1, boxShadow:`0 0 0 2px #fff, 0 0 0 3px ${e.color}44` }}/>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#1c1c1e' }}>{e.label}</div>
            <div style={{ fontSize:10, color:'#8E8E93' }}>{fmtDate(e.time)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Improved bar chart (last-7-days or monthly) ──────────────────────────────
function bucketTickets(tickets, range) {
  if (range === 'week') {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      days.push({ key: d.toLocaleDateString('en-IN',{day:'2-digit',month:'short'}),
        date: d.toDateString(), open:0, closed:0, sortKey: d.getTime() });
    }
    tickets.forEach(t => {
      if (!t.created_at) return;
      const b = days.find(d => d.date === new Date(t.created_at).toDateString());
      if (!b) return;
      if (isTicketDone(t.status)) b.closed++; else b.open++;
    });
    return days;
  }
  const buckets = {};
  tickets.forEach(t => {
    if (!t.created_at) return;
    const d = new Date(t.created_at); if (isNaN(d)) return;
    const key = d.toLocaleString('en-IN',{month:'short',year:'2-digit'});
    const sk  = d.getFullYear()*100 + d.getMonth();
    if (!buckets[key]) buckets[key] = { key, open:0, closed:0, sortKey:sk };
    if (isTicketDone(t.status)) buckets[key].closed++; else buckets[key].open++;
  });
  return Object.values(buckets).sort((a,b)=>a.sortKey-b.sortKey).slice(-8);
}

function TicketBarChart({ data }) {
  if (!data.length) return (
    <div style={{ textAlign:'center', padding:'28px 0', color:'#C7C7CC', fontSize:12 }}>
      No tickets in this period
    </div>
  );
  const maxV = Math.max(1, ...data.map(d=>Math.max(d.open,d.closed)));
  const chartH = 110;
  const gridN  = 4;
  return (
    <div style={{ position:'relative', paddingLeft:30, paddingTop:4 }}>
      {/* Y-axis grid */}
      {Array.from({length:gridN+1}).map((_,i) => {
        const val = Math.round((maxV/gridN)*(gridN-i));
        return (
          <div key={i} style={{ position:'absolute', left:0, right:0,
            top:`${(i/gridN)*chartH}px`, display:'flex', alignItems:'center', pointerEvents:'none' }}>
            <span style={{ fontSize:8, color:'#C7C7CC', minWidth:24, textAlign:'right', paddingRight:4 }}>{val}</span>
            <div style={{ flex:1, borderTop:'1px dashed rgba(0,0,0,.06)' }}/>
          </div>
        );
      })}
      {/* Bars */}
      <div style={{ display:'flex', alignItems:'flex-end', gap:5, height:chartH+26 }}>
        {data.map(d => (
          <div key={d.key} style={{ flex:1, display:'flex', flexDirection:'column',
            alignItems:'center', height:'100%', justifyContent:'flex-end' }}>
            <div style={{ display:'flex', gap:2, alignItems:'flex-end', height:chartH, width:'100%', justifyContent:'center' }}>
              {/* Open bar */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', height:'100%' }}>
                {d.open > 0 && <span style={{ fontSize:8, fontWeight:800, color:'#FF9F0A', marginBottom:2 }}>{d.open}</span>}
                <div title={`${d.open} open`} style={{ width:10, borderRadius:'4px 4px 0 0',
                  background:'linear-gradient(180deg,#FF9F0A,#CC7A00)',
                  height:`${Math.max(3,(d.open/maxV)*chartH)}px`, transition:'height .4s ease' }}/>
              </div>
              {/* Closed bar */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', height:'100%' }}>
                {d.closed > 0 && <span style={{ fontSize:8, fontWeight:800, color:'#34C759', marginBottom:2 }}>{d.closed}</span>}
                <div title={`${d.closed} closed`} style={{ width:10, borderRadius:'4px 4px 0 0',
                  background:'linear-gradient(180deg,#34C759,#248A3D)',
                  height:`${Math.max(3,(d.closed/maxV)*chartH)}px`, transition:'height .4s ease' }}/>
              </div>
            </div>
            <div style={{ fontSize:8, color:'#8E8E93', fontWeight:600, textAlign:'center',
              marginTop:4, whiteSpace:'nowrap' }}>{d.key}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Manager Drilldown modal ──────────────────────────────────────────────────
function ManagerDrilldown({ manager, tickets, onClose }) {
  const [range, setRange] = useState('week');
  const mgrTickets = tickets.filter(t =>
    (t.assigned_agent||'').toLowerCase() === manager.email.toLowerCase()
  );
  const ces      = getCEsForManager(manager.id);
  const chartData = bucketTickets(mgrTickets, range);
  const open     = mgrTickets.filter(t => !isTicketDone(t.status)).length;
  const closed   = mgrTickets.filter(t => isTicketDone(t.status)).length;
  const breach   = mgrTickets.filter(t => { const h=ageHours(t); return h&&h>48&&!isTicketDone(t.status); }).length;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
      backdropFilter:'blur(16px)', zIndex:310, display:'flex', alignItems:'center', justifyContent:'center',
      animation:'supFadeUp .2s ease' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:26,
        width:640, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,.22)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif" }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:13, flexShrink:0,
              background:'linear-gradient(135deg,#007AFF,#0056d3)',
              display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:18, fontWeight:800 }}>
              {manager.name[0]}
            </div>
            <div>
              <div style={{ fontSize:17, fontWeight:800, color:'#1c1c1e' }}>{manager.name}</div>
              <div style={{ fontSize:12, color:'#8E8E93', marginTop:2 }}>
                {manager.region} · {ces.length} Field Engineers
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:'50%', border:'none',
            background:'rgba(0,0,0,.07)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {Ico.Close}
          </button>
        </div>

        {/* KPI row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
          {[
            { label:'Total',   value:mgrTickets.length, color:'#007AFF' },
            { label:'Open',    value:open,               color:'#FF9F0A' },
            { label:'Closed',  value:closed,             color:'#34C759' },
            { label:'SLA Breach', value:breach,          color:'#FF3B30' },
          ].map(s => (
            <div key={s.label} style={{ background:'#fafafa', borderRadius:12, padding:'12px 14px',
              border:'1px solid rgba(0,0,0,.06)' }}>
              <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:11, color:'#8E8E93', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* CEs */}
        <div style={{ fontSize:12, fontWeight:700, color:'#1c1c1e', marginBottom:10 }}>Field Engineers</div>
        <div style={{ display:'flex', flexDirection:'column', gap:7, marginBottom:22 }}>
          {ces.length === 0 ? (
            <div style={{ textAlign:'center', padding:20, color:'#8E8E93', fontSize:12,
              background:'#fafafa', borderRadius:12 }}>No field engineers assigned yet</div>
          ) : ces.map(ce => {
            const ceTickets = mgrTickets.filter(t=>(t.ce_email||'').toLowerCase()===ce.email.toLowerCase());
            const ceOpen    = ceTickets.filter(t=>!isTicketDone(t.status)).length;
            const cePct     = ceTickets.length > 0 ? Math.round((ceTickets.filter(t=>isTicketDone(t.status)).length/ceTickets.length)*100) : 0;
            return (
              <div key={ce.email} style={{ display:'flex', alignItems:'center', gap:12,
                padding:'11px 14px', borderRadius:12, background:'#fafafa', border:'1px solid rgba(0,0,0,.05)' }}>
                <div style={{ width:34, height:34, borderRadius:10, flexShrink:0,
                  background:'linear-gradient(135deg,#AF52DE,#8B36B0)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontSize:13, fontWeight:800 }}>
                  {ce.name[0].toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>
                      {ce.code ? `${ce.code} · ` : ''}{ce.name}
                    </div>
                    <span style={{ fontSize:11, fontWeight:700,
                      color: ceOpen>0 ? '#FF9F0A' : '#34C759' }}>
                      {ceOpen > 0 ? `${ceOpen} open` : 'All clear'}
                    </span>
                  </div>
                  <div style={{ background:'rgba(0,0,0,.06)', borderRadius:999, height:4, overflow:'hidden' }}>
                    <div style={{ height:'100%', borderRadius:999, background:'#34C759',
                      width:`${cePct}%`, transition:'width .5s ease' }}/>
                  </div>
                </div>
                <span style={{ fontSize:12, fontWeight:800, color:'#1c1c1e', minWidth:20, textAlign:'right' }}>
                  {ceTickets.length}
                </span>
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#1c1c1e' }}>Open vs Closed</div>
          <div style={{ display:'flex', gap:3, background:'#f0f4f8', borderRadius:10, padding:3 }}>
            {['week','month'].map(r => (
              <button key={r} onClick={()=>setRange(r)} style={{
                padding:'5px 12px', borderRadius:8, border:'none', fontSize:11, fontWeight:700,
                cursor:'pointer', fontFamily:'inherit', textTransform:'capitalize',
                background: range===r ? '#fff' : 'transparent',
                color: range===r ? '#007AFF' : '#8E8E93',
                boxShadow: range===r ? '0 1px 5px rgba(0,0,0,.1)' : 'none',
              }}>{r}</button>
            ))}
          </div>
        </div>
        <div style={{ background:'#fafafa', borderRadius:14, padding:'14px 10px 8px',
          border:'1px solid rgba(0,0,0,.05)' }}>
          <TicketBarChart data={chartData}/>
          <div style={{ display:'flex', gap:16, justifyContent:'center', marginTop:10 }}>
            {[['#FF9F0A','Open'],['#34C759','Closed']].map(([c,l])=>(
              <span key={l} style={{ fontSize:10, color:'#8E8E93', display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ width:8, height:8, borderRadius:2, background:c }}/>  {l}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Ticket Detail Modal ───────────────────────────────────────────────────────
function TicketDetail({ ticket, onClose, onUpdate }) {
  const [managers, setManagers] = useState([]);
  const [overrideMgr, setOverrideMgr] = useState(ticket.assigned_agent || '');
  const [newPriority, setNewPriority]  = useState(ticket.priority || 'MEDIUM');
  const [newStatus,   setNewStatus]    = useState('');
  const [saving, setSaving]   = useState(false);
  const [saved,  setSaved]    = useState(false);
  const [saveError, setSaveError] = useState('');

  const age     = ageHours(ticket);
  const slaBr   = age != null && age > 48 && !isTicketDone(ticket.status);

  useEffect(() => {
    fetch(`${API_BASE}/api/accounts`)
      .then(r=>r.json())
      .then(data => {
        const mgrs = (Array.isArray(data)?data:[])
          .filter(a=>a.role==='service_manager');
        setManagers(mgrs);
      }).catch(()=>{});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaveError(''); setSaved(false);
    try {
      const body = { ticket_id: ticket.ticket_id, priority: newPriority };
      if (overrideMgr) {
        const m = managers.find(m=>m.email===overrideMgr);
        body.assigned_agent      = overrideMgr;
        body.assigned_agent_name = m?.fullName || overrideMgr;
        body.status = 'ASSIGNED_TO_MANAGER';
      }
      if (newStatus) body.status = newStatus;
      const res = await fetch(`${API_BASE}/api/tickets`, {
        method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      setSaved(true);
      setTimeout(()=>{ onUpdate(); onClose(); }, 800);
    } catch(e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  const sel = { width:'100%', padding:'9px 12px', borderRadius:10, border:'1.5px solid rgba(0,0,0,.1)',
    fontSize:13, fontFamily:'inherit', background:'#fafafa', outline:'none' };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
      backdropFilter:'blur(16px)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center',
      animation:'supFadeUp .2s ease' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:26,
        width:540, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,.22)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif" }}>

        {/* Ticket header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
              <span style={{ fontSize:10, color:'#aeaeb2', fontFamily:'monospace',
                fontWeight:700, letterSpacing:.8 }}>{ticket.ticket_id}</span>
              <StatusBadge status={ticket.status}/>
              <PriBadge priority={ticket.priority}/>
              {slaBr && (
                <span style={{ fontSize:9, fontWeight:800, color:'#FF3B30',
                  background:'rgba(255,59,48,.1)', padding:'2px 8px', borderRadius:20 }}>
                  🔴 SLA {fmtHrs(age)} overdue
                </span>
              )}
            </div>
            <div style={{ fontSize:18, fontWeight:800, color:'#1c1c1e', lineHeight:1.3 }}>
              {ticket.issue}
            </div>
          </div>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:'50%', border:'none',
            background:'rgba(0,0,0,.07)', cursor:'pointer', display:'flex', alignItems:'center',
            justifyContent:'center', flexShrink:0, marginLeft:8 }}>
            {Ico.Close}
          </button>
        </div>

        {/* SLA bar */}
        {age != null && (
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:10,
              color:'#8E8E93', marginBottom:4 }}>
              <span>SLA Progress (48h)</span>
              <span style={{ color: slaBr?'#FF3B30':age>33?'#FF9F0A':'#34C759', fontWeight:700 }}>
                {slaBr ? `${fmtHrs(age)} overdue` : `${fmtHrs(48-age)} remaining`}
              </span>
            </div>
            <SLABar hours={age} done={isTicketDone(ticket.status)}/>
          </div>
        )}

        {/* Lifecycle strip */}
        <LifecycleStrip status={ticket.status}/>

        {/* Info grid */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, margin:'14px 0' }}>
          {[
            ['Customer',  ticket.customer_name || '—'],
            ['Device',    ticket.device || '—'],
            ['MAC',       ticket.macid  || '—', true],
            ['Location',  ticket.location || '—'],
            ['Manager',   ticket.assigned_agent_name || 'Unassigned'],
            ['CE',        ticket.ce_name || '—'],
            ['Created',   fmtDate(ticket.created_at)],
            ['Updated',   fmtDate(ticket.updated_at)],
          ].map(([l,v,mono]) => (
            <div key={l} style={{ background:'rgba(0,0,0,.025)', borderRadius:10, padding:'8px 12px' }}>
              <div style={{ fontSize:10, color:'#8E8E93', fontWeight:600, marginBottom:3 }}>{l}</div>
              <div style={{ fontSize:12, color:'#1c1c1e', fontWeight:700,
                fontFamily:mono?'monospace':'inherit', wordBreak:'break-all' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Notes */}
        {ticket.notes && (
          <div style={{ padding:'12px 14px', background:'rgba(0,122,255,.04)',
            borderRadius:12, border:'1px solid rgba(0,122,255,.1)', marginBottom:14 }}>
            <div style={{ fontSize:10, color:'#007AFF', fontWeight:700, letterSpacing:.6,
              textTransform:'uppercase', marginBottom:6 }}>Field Notes</div>
            <div style={{ fontSize:13, color:'#48484a', lineHeight:1.6 }}>{ticket.notes}</div>
          </div>
        )}

        {/* Timeline */}
        <Timeline ticket={ticket}/>

        {/* Admin Controls */}
        {!isTicketDone(ticket.status) && (
          <div style={{ marginTop:16, padding:'16px', background:'rgba(0,122,255,.03)',
            borderRadius:14, border:'1px solid rgba(0,122,255,.1)' }}>
            <div style={{ fontSize:11, color:'#007AFF', fontWeight:700, marginBottom:14,
              letterSpacing:.8, textTransform:'uppercase' }}>Admin Controls</div>

            <label style={{ fontSize:11, fontWeight:600, color:'#48484a', marginBottom:5, display:'block' }}>
              Assign to Manager
            </label>
            <select value={overrideMgr} onChange={e=>setOverrideMgr(e.target.value)} style={{...sel,marginBottom:12}}>
              <option value="">— Keep current —</option>
              {managers.map(m=><option key={m.email} value={m.email}>{m.fullName || m.email}</option>)}
            </select>

            <label style={{ fontSize:11, fontWeight:600, color:'#48484a', marginBottom:5, display:'block' }}>
              Priority
            </label>
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              {['HIGH','MEDIUM','LOW'].map(p => {
                const cfg = PRIORITY[p];
                return (
                  <button key={p} onClick={()=>setNewPriority(p)} style={{
                    flex:1, padding:'8px', borderRadius:10, cursor:'pointer', fontFamily:'inherit',
                    border: newPriority===p ? `2px solid ${cfg.color}` : '1.5px solid rgba(0,0,0,.08)',
                    background: newPriority===p ? cfg.bg : '#fafafa',
                    fontSize:12, fontWeight:700, color:cfg.color,
                  }}>{cfg.label}</button>
                );
              })}
            </div>

            {saveError && (
              <div style={{ fontSize:12, color:'#FF3B30', marginBottom:10, padding:'8px 12px',
                background:'rgba(255,59,48,.05)', borderRadius:9 }}>{saveError}</div>
            )}

            <button onClick={handleSave} disabled={saving||saved} style={{
              width:'100%', padding:12, borderRadius:12, border:'none',
              background: saved ? 'linear-gradient(135deg,#34C759,#248A3D)'
                : 'linear-gradient(135deg,#007AFF,#0056d3)',
              color:'#fff', fontWeight:700, fontSize:14, cursor:saving?'wait':'pointer',
              fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              transition:'all .25s', opacity:saving?.8:1,
            }}>
              {saved ? <>{Ico.Check} Saved!</> : saving ? (
                <><div style={{ width:14, height:14, border:'2px solid rgba(255,255,255,.4)',
                  borderTopColor:'#fff', borderRadius:'50%', animation:'spin .6s linear infinite' }}/> Saving…</>
              ) : 'Save Changes'}
            </button>
          </div>
        )}

        <button onClick={onClose} style={{ width:'100%', marginTop:12, padding:11, borderRadius:12,
          border:'1.5px solid rgba(0,0,0,.09)', background:'#fff', color:'#636366',
          fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Support Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Support() {
  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [loaded,       setLoaded]       = useState(false);
  const [error,        setError]        = useState('');
  const [filter,       setFilter]       = useState('All');
  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null);
  const [drillManager, setDrillManager] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_BASE}/api/tickets`,{headers:{'ngrok-skip-browser-warning':'true'}});
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = await res.json();
      const raw  = json.tickets || json.data || (Array.isArray(json)?json:[]);
      setTickets(raw); setLoaded(true);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!loaded) return;
    const t = setInterval(fetchTickets, 30000);
    return () => clearInterval(t);
  }, [loaded, fetchTickets]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const total      = tickets.length;
  const newCount   = tickets.filter(t=>['NEW','ASSIGNED_TO_MANAGER'].includes(normaliseStatus(t.status))).length;
  const inProg     = tickets.filter(t=>['ASSIGNED_TO_CE','ASSIGNED_TO_VENDOR','IN_PROGRESS','WAITING_FOR_PARTS','WORK_COMPLETED'].includes(normaliseStatus(t.status))).length;
  const pending    = tickets.filter(t=>isInValidation(t.status)).length;
  const resolved   = tickets.filter(t=>isTicketDone(t.status)).length;
  const breaches   = tickets.filter(t=>{ const h=ageHours(t); return h&&h>48&&!isTicketDone(t.status); }).length;
  const avgRes     = (() => {
    const done = tickets.filter(t=>isTicketDone(t.status)&&t.created_at&&(t.end_time||t.resolved_at||t.closed_at));
    if (!done.length) return null;
    const sum = done.reduce((s,t)=>{
      const e=new Date(t.end_time||t.resolved_at||t.closed_at).getTime();
      return s+(e-new Date(t.created_at).getTime())/36e5;
    },0);
    return Math.round((sum/done.length)*10)/10;
  })();

  const managerSummaries = getManagerSummaries().map(mgr => {
    const mine = tickets.filter(t=>(t.assigned_agent||'').toLowerCase()===mgr.email.toLowerCase());
    return { ...mgr, total:mine.length,
      open:mine.filter(t=>!isTicketDone(t.status)).length,
      closed:mine.filter(t=>isTicketDone(t.status)).length };
  });

  // Filter + search
  const FILTERS = ['All','New','In Progress','Pending Validation','Resolved','SLA Breach'];
  const filtered = tickets.filter(t => {
    const s = normaliseStatus(t.status);
    const matchFilter = (
      filter === 'All'               ? true :
      filter === 'New'               ? ['NEW','ASSIGNED_TO_MANAGER'].includes(s) :
      filter === 'In Progress'       ? ['ASSIGNED_TO_CE','ASSIGNED_TO_VENDOR','IN_PROGRESS','WAITING_FOR_PARTS','WORK_COMPLETED'].includes(s) :
      filter === 'Pending Validation'? isInValidation(t.status) :
      filter === 'Resolved'          ? isTicketDone(t.status) :
      filter === 'SLA Breach'        ? (()=>{ const h=ageHours(t); return h&&h>48&&!isTicketDone(t.status); })() :
      true
    );
    if (!matchFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (t.ticket_id||'').toLowerCase().includes(q) ||
      (t.issue||'').toLowerCase().includes(q) ||
      (t.customer_name||'').toLowerCase().includes(q) ||
      (t.device||'').toLowerCase().includes(q) ||
      (t.assigned_agent_name||'').toLowerCase().includes(q);
  });

  const filterCount = {
    'All': total, 'New': newCount, 'In Progress': inProg,
    'Pending Validation': pending, 'Resolved': resolved, 'SLA Breach': breaches,
  };

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:1000 }}>
      <style>{STYLES}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
        marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <img src="/havells-logo.png" alt="Havells" style={{ height:44, objectFit:'contain', marginBottom:10, display:'block' }}/>
          <h1 style={{ fontSize:26, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, margin:0 }}>Support</h1>
          <p style={{ color:'#8E8E93', fontSize:13, marginTop:4, margin:0 }}>
            Admin → Manager → Field Engineer → Vendor · Full chain monitored here
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {loaded && (
            <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11,
              color:'#8E8E93', background:'rgba(0,0,0,.04)', padding:'5px 10px', borderRadius:20 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:'#34C759',
                animation:'pulse 1.5s ease infinite' }}/>
              Live · auto-refresh 30s
            </div>
          )}
          <button onClick={fetchTickets} disabled={loading} style={{
            display:'inline-flex', alignItems:'center', gap:7, padding:'9px 18px',
            borderRadius:12, border:'none',
            background:'linear-gradient(135deg,#007AFF,#0056d3)',
            color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer',
            fontFamily:'inherit', boxShadow:'0 4px 14px rgba(0,122,255,.25)',
            opacity:loading?.65:1,
          }}>
            <span style={loading?{animation:'spin .8s linear infinite',display:'flex'}:{display:'flex'}}>
              {Ico.Refresh}
            </span>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background:'rgba(255,59,48,.05)', border:'1px solid rgba(255,59,48,.15)',
          borderRadius:12, padding:'11px 16px', marginBottom:16, color:'#FF3B30', fontSize:13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:20 }}>
        {[
          { label:'Total',       value:total,   color:'#007AFF' },
          { label:'New',         value:newCount, color:'#FF3B30' },
          { label:'In Progress', value:inProg,   color:'#FF9F0A' },
          { label:'Validation',  value:pending,  color:'#AF52DE' },
          { label:'Resolved',    value:resolved, color:'#34C759' },
          { label:'SLA Breach',  value:breaches, color:'#FF3B30' },
        ].map(s => (
          <div key={s.label} className="s-card" onClick={()=>setFilter(s.label==='Validation'?'Pending Validation':s.label==='SLA Breach'?'SLA Breach':s.label)} style={{
            background:'#fff', borderRadius:14, padding:'14px 16px', cursor:'pointer',
            boxShadow:'0 2px 8px rgba(0,0,0,.05)', border:`1.5px solid ${filter===s.label||(s.label==='Validation'&&filter==='Pending Validation')?s.color+'44':'rgba(0,0,0,.06)'}`,
            animation:'supFadeUp .4s ease both',
          }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:s.color, marginBottom:10,
              boxShadow: s.value>0&&s.label!=='Total'&&s.label!=='Resolved' ? `0 0 8px ${s.color}88` : 'none' }}/>
            <div style={{ fontSize:26, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5 }}>{s.value}</div>
            <div style={{ fontSize:11, color:'#8E8E93', marginTop:3, fontWeight:500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Avg resolution ── */}
      {avgRes != null && (
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18,
          padding:'10px 16px', background:'rgba(52,199,89,.05)',
          borderRadius:12, border:'1px solid rgba(52,199,89,.15)' }}>
          <span style={{ fontSize:20 }}>⏱</span>
          <div>
            <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>
              Avg Resolution Time: {fmtHrs(avgRes)}
            </span>
            <span style={{ fontSize:11, color:'#8E8E93', marginLeft:8 }}>
              {avgRes <= 24 ? '✅ Within SLA' : avgRes <= 48 ? '⚠️ Near SLA' : '🔴 SLA breach risk'}
            </span>
          </div>
        </div>
      )}

      {/* ── Manager Cards ── */}
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e', marginBottom:12 }}>
          Manager Performance
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:10 }}>
          {managerSummaries.map(mgr => {
            const resolution = mgr.total > 0 ? Math.round((mgr.closed/mgr.total)*100) : 0;
            return (
              <div key={mgr.id} className="s-mgr" onClick={()=>setDrillManager(mgr)} style={{
                background:'#fff', borderRadius:14, padding:'14px 16px',
                boxShadow:'0 1px 6px rgba(0,0,0,.05)', border:'1px solid rgba(0,0,0,.06)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
                    background:'linear-gradient(135deg,#007AFF,#0056d3)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#fff', fontSize:14, fontWeight:800 }}>
                    {mgr.name[0]}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e',
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{mgr.name}</div>
                    <div style={{ fontSize:10, color:'#8E8E93' }}>{mgr.region}</div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:11, color:'#8E8E93' }}><strong style={{color:'#1c1c1e'}}>{mgr.total}</strong> total</span>
                  <span style={{ fontSize:11, color:'#FF9F0A' }}><strong>{mgr.open}</strong> open</span>
                  <span style={{ fontSize:11, color:'#34C759' }}><strong>{mgr.closed}</strong> closed</span>
                </div>
                {/* Resolution rate bar */}
                <div style={{ background:'rgba(0,0,0,.06)', borderRadius:999, height:4, overflow:'hidden' }}>
                  <div style={{ height:'100%', borderRadius:999, background:'linear-gradient(90deg,#34C759,#248A3D)',
                    width:`${resolution}%`, transition:'width .5s ease' }}/>
                </div>
                <div style={{ fontSize:9, color:'#8E8E93', marginTop:4, textAlign:'right' }}>
                  {resolution}% resolution rate
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Filter tabs + Search ── */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:3, background:'#f0f4f8', borderRadius:12, padding:3, flexWrap:'wrap' }}>
          {FILTERS.map(f => (
            <button key={f} onClick={()=>setFilter(f)} className="s-flt" style={{
              padding:'7px 13px', borderRadius:9, border:'none', fontSize:12,
              fontWeight:600, cursor:'pointer', fontFamily:'inherit',
              background: filter===f ? '#fff' : 'transparent',
              color: filter===f ? '#007AFF' : '#8E8E93',
              boxShadow: filter===f ? '0 1px 6px rgba(0,0,0,.09)' : 'none',
            }}>
              {f} {filterCount[f]>0 ? <span style={{ fontSize:10, fontWeight:800,
                color: filter===f?'#007AFF':'#8E8E93' }}>({filterCount[f]})</span> : ''}
            </button>
          ))}
        </div>
        {/* Search */}
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search tickets, devices, customers…"
          style={{ flex:1, minWidth:200, padding:'9px 14px', borderRadius:12,
            border:'1.5px solid rgba(0,0,0,.09)', fontSize:13, fontFamily:'inherit',
            background:'#fff', outline:'none', color:'#1c1c1e' }}/>
      </div>

      {/* ── Loading ── */}
      {loading && tickets.length === 0 && (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#8E8E93' }}>
          <div style={{ width:28, height:28, borderRadius:'50%', border:'3px solid rgba(0,122,255,.12)',
            borderTopColor:'#007AFF', animation:'spin .7s linear infinite', margin:'0 auto 14px' }}/>
          <p style={{ fontSize:13, fontWeight:500 }}>Loading tickets…</p>
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#8E8E93',
          background:'#fff', borderRadius:18, border:'1px solid rgba(0,0,0,.06)',
          boxShadow:'0 2px 10px rgba(0,0,0,.04)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🎫</div>
          <p style={{ fontSize:14, fontWeight:600, color:'#3a3a3c', marginBottom:4 }}>
            {search ? 'No matching tickets' : 'No tickets in this category'}
          </p>
          <p style={{ fontSize:13 }}>
            {search ? `Try a different search term` : 'Tickets raised from the mobile app will appear here'}
          </p>
        </div>
      )}

      {/* ── Ticket list ── */}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {filtered.map((t, i) => {
          const age    = ageHours(t);
          const slaBr  = age != null && age > 48 && !isTicketDone(t.status);
          const unassigned = !t.assigned_agent;
          return (
            <div key={(t.ticket_id||'')+i} className="s-row" onClick={()=>setSelected(t)} style={{
              background:'#fff', borderRadius:14, padding:'14px 18px',
              boxShadow: slaBr ? '0 2px 14px rgba(255,59,48,.12)' : '0 1px 6px rgba(0,0,0,.06)',
              border: slaBr ? '1.5px solid rgba(255,59,48,.22)' : unassigned ? '1.5px solid rgba(255,159,10,.2)' : '1px solid rgba(0,0,0,.06)',
              animation:`supFadeUp .3s ease ${i*.04}s both`,
            }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                {/* Left dot */}
                <div style={{ paddingTop:3, flexShrink:0 }}>
                  <div style={{ width:9, height:9, borderRadius:'50%',
                    background:getStatusConfig(t.status).dot,
                    boxShadow: !isTicketDone(t.status)?`0 0 8px ${getStatusConfig(t.status).dot}66`:'none' }}/>
                </div>

                {/* Content */}
                <div style={{ flex:1, minWidth:0 }}>
                  {/* Row 1: ID + badges */}
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5, flexWrap:'wrap' }}>
                    <span style={{ fontSize:10, color:'#aeaeb2', fontFamily:'monospace',
                      fontWeight:700, letterSpacing:.5 }}>{t.ticket_id}</span>
                    <StatusBadge status={t.status} small/>
                    <PriBadge priority={t.priority}/>
                    {slaBr && (
                      <span style={{ fontSize:9, fontWeight:800, color:'#FF3B30',
                        background:'rgba(255,59,48,.1)', padding:'2px 8px', borderRadius:20 }}>
                        🔴 {fmtHrs(age)} overdue
                      </span>
                    )}
                    {unassigned && !isTicketDone(t.status) && (
                      <span style={{ fontSize:9, fontWeight:700, color:'#FF9F0A',
                        background:'rgba(255,159,10,.1)', padding:'2px 8px', borderRadius:20 }}>
                        ⚡ Needs assignment
                      </span>
                    )}
                  </div>

                  {/* Row 2: Issue */}
                  <div style={{ fontSize:14, fontWeight:700, color:'#1c1c1e', marginBottom:6,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {t.issue}
                  </div>

                  {/* Row 3: Meta */}
                  <div style={{ display:'flex', gap:14, fontSize:11, color:'#8E8E93',
                    flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ display:'flex', alignItems:'center', gap:3 }}>
                      {Ico.Device} {t.device || '—'}
                    </span>
                    <span style={{ display:'flex', alignItems:'center', gap:3 }}>
                      {Ico.Person} {t.customer_name || '—'}
                    </span>
                    <span style={{ display:'flex', alignItems:'center', gap:3 }}>
                      {Ico.Team} {t.assigned_agent_name || 'Unassigned'}
                    </span>
                    <span style={{ display:'flex', alignItems:'center', gap:3 }}>
                      {Ico.Clock} {fmtDate(t.updated_at)}
                    </span>
                    {age != null && (
                      <span style={{ color: slaBr?'#FF3B30':age>33?'#FF9F0A':'#8E8E93' }}>
                        Age: {fmtHrs(age)}
                      </span>
                    )}
                  </div>

                  {/* SLA mini bar (only for open tickets) */}
                  {age != null && !isTicketDone(t.status) && (
                    <div style={{ marginTop:8 }}>
                      <SLABar hours={age} done={false}/>
                    </div>
                  )}
                </div>

                {/* Chevron */}
                <div style={{ flexShrink:0, alignSelf:'center' }}>{Ico.Chevron}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modals */}
      {selected && (
        <TicketDetail key={selected.ticket_id} ticket={selected}
          onClose={()=>setSelected(null)} onUpdate={fetchTickets}/>
      )}
      {drillManager && (
        <ManagerDrilldown manager={drillManager} tickets={tickets}
          onClose={()=>setDrillManager(null)}/>
      )}
    </div>
  );
}
